import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Mail, KeyRound, Copy, Loader2, UserCheck, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  ativo: "bg-emerald-100 text-emerald-700",
  convite_enviado: "bg-amber-100 text-amber-700",
  expirado: "bg-red-100 text-red-700",
  inativo: "bg-zinc-100 text-zinc-600",
};
const STATUS_LABELS = {
  ativo: "Ativo",
  convite_enviado: "Convite enviado",
  expirado: "Expirado",
  inativo: "Inativo",
};

const ROLES_PODEM_LIBERAR = ["diretor", "vendedor", "operador", "financeiro", "supervisao"];

export function AcessoPortalCard({ clienteLocativoId, clienteNome }) {
  const { user } = useAuth();
  const podeLiberar = ROLES_PODEM_LIBERAR.includes(user?.role);
  const [acessos, setAcessos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const carregar = () => {
    if (!clienteLocativoId) return;
    setLoading(true);
    api.get(`/admin/usuarios/cliente-locativo/${clienteLocativoId}/acessos`)
      .then((r) => setAcessos(r.data.items || []))
      .catch(() => setAcessos([]))
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [clienteLocativoId]);

  if (!podeLiberar) return null;

  return (
    <>
      <Card className="p-5" data-testid="acesso-portal-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold flex items-center gap-2 text-zinc-700">
            <UserCheck className="w-4 h-4 text-orange-600" /> Acesso ao portal
          </h3>
          <Button
            size="sm"
            onClick={() => setModalOpen(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="btn-liberar-acesso"
          >
            <KeyRound className="w-3.5 h-3.5 mr-1" /> Liberar acesso
          </Button>
        </div>
        {loading ? (
          <div className="py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline text-zinc-400" /></div>
        ) : acessos.length === 0 ? (
          <p className="text-xs text-zinc-400 py-3 text-center">Nenhum acesso liberado ainda.</p>
        ) : (
          <div className="space-y-2">
            {acessos.map((a) => (
              <div key={a.id} className="flex items-center justify-between text-xs border border-zinc-100 rounded-md px-3 py-2" data-testid={`acesso-${a.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{a.name}</div>
                  <div className="font-mono text-[11px] text-zinc-500 truncate">{a.email}</div>
                </div>
                <Badge className={`${STATUS_COLORS[a.status_acesso] || "bg-zinc-100"} text-[10px]`}>
                  {STATUS_LABELS[a.status_acesso] || a.status_acesso}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </Card>

      <ModalLiberarAcesso
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        clienteLocativoId={clienteLocativoId}
        clienteNome={clienteNome}
        onSuccess={carregar}
      />
    </>
  );
}

function ModalLiberarAcesso({ open, onClose, clienteLocativoId, clienteNome, onSuccess }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState(clienteNome || "");
  const [dias, setDias] = useState(60);
  const [enviarEmail, setEnviarEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [conviteUrl, setConviteUrl] = useState("");

  useEffect(() => {
    if (open) {
      setName(clienteNome || "");
      setEmail("");
      setDias(60);
      setEnviarEmail(true);
      setConviteUrl("");
    }
  }, [open, clienteNome]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return toast.error("E-mail inválido");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/usuarios/liberar-acesso-cliente", {
        cliente_locativo_id: clienteLocativoId,
        email: email.toLowerCase(),
        name,
        dias_validade: dias,
        enviar_email: enviarEmail,
      });
      toast.success(`Acesso ${data.criado ? "criado" : "atualizado"}! Convite enviado.`);
      setConviteUrl(data.convite_url || "");
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao liberar acesso");
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = () => {
    navigator.clipboard?.writeText(conviteUrl).then(() => toast.success("Link copiado"));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="modal-liberar-acesso">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-orange-600" /> Liberar acesso ao portal
          </DialogTitle>
        </DialogHeader>
        {conviteUrl ? (
          <div className="space-y-4 py-4">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md">
              <p className="text-sm font-medium text-emerald-800">✓ Acesso criado com sucesso</p>
              <p className="text-xs text-emerald-700 mt-1">
                {enviarEmail ? "Um e-mail de convite foi enviado." : "Compartilhe o link abaixo com o cliente."}
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase">Link de convite (válido 48h)</Label>
              <div className="flex gap-2 mt-1">
                <Input value={conviteUrl} readOnly className="text-xs font-mono" />
                <Button size="sm" onClick={copyUrl} variant="outline">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">Pode ser enviado via WhatsApp se necessário.</p>
            </div>
            <DialogFooter>
              <Button onClick={onClose}>Fechar</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase">Cliente</Label>
              <Input value={clienteNome} readOnly className="bg-zinc-50" />
            </div>
            <div>
              <Label className="text-xs uppercase">Nome do contato</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="liberar-name" />
            </div>
            <div>
              <Label className="text-xs uppercase">E-mail de acesso *</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value.toLowerCase())} required data-testid="liberar-email" />
            </div>
            <div>
              <Label className="text-xs uppercase">Validade (dias)</Label>
              <Input type="number" min={1} max={365} value={dias} onChange={(e) => setDias(parseInt(e.target.value, 10) || 60)} />
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={enviarEmail} onChange={(e) => setEnviarEmail(e.target.checked)} />
              <Mail className="w-3 h-3" /> Enviar e-mail de convite automaticamente
            </label>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={busy} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="liberar-submit">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCcw className="w-3.5 h-3.5 mr-1" />}
                Criar acesso e enviar convite
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
